import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  CartItem, CartResponse, Product, ProductVariant, CustomizationOption, CouponValidationResponse,
} from '../../types';
import api, { validateCoupon } from '../../services/api';
import { GROUP_COUPON_REMOVED_NOTICE } from '../../utils/groupDiscount';

interface AppliedCoupon {
  code: string;
  discountAmount: number;
  message: string;
  /** A kuponra jogosult kosár-tételek (null = mind) — a kedvezmény utáni ÁFA-számításhoz. */
  eligibleItemIds: number[] | null;
  /** GTP (GT Pont beváltás) kupon — csoport-kedvezménnyel is használható. */
  isLoyalty: boolean;
}


interface CartState {
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
  isOpen: boolean;
  isGuest: boolean; // Flag to track if cart is guest cart
  appliedCoupon: AppliedCoupon | null;
  couponLoading: boolean;
  couponError: string | null;
  /** Csoport-kedvezmény (szerver-kosár): összeg (egész Ft) és a sor címkéje. */
  groupDiscountAmount: number;
  groupDiscountLabel: string | null;
  /** Van csoport-kedvezményes tétel → csak GTP kupon használható. */
  couponBlocked: boolean;
  /** Jelzés a vevőnek, ha a kupont a csoport-kedvezmény miatt levettük. */
  couponNotice: string | null;
  // Optimista szerver-műveletek visszagörgetési adatai, a kérés requestId-ja
  // szerint (a pending menti a változás előtti állapotot, a rejected visszaállít).
  pendingRollback?: Record<string, CartRollback>;
}

interface CartRollback {
  removed?: { item: CartItem; index: number };
  quantity?: { itemId: number; quantity: number; totalPrice: number };
  clearedItems?: CartItem[];
}

// Load guest cart from localStorage
const loadGuestCart = (): CartItem[] => {
  try {
    const savedCart = localStorage.getItem('guestCart');
    return savedCart ? JSON.parse(savedCart) : [];
  } catch (error) {
    console.error('Error loading guest cart:', error);
    return [];
  }
};

// Save guest cart to localStorage
const saveGuestCart = (items: CartItem[]) => {
  try {
    localStorage.setItem('guestCart', JSON.stringify(items));
  } catch (error) {
    console.error('Error saving guest cart:', error);
  }
};

// Clear guest cart from localStorage
const clearGuestCartStorage = () => {
  try {
    localStorage.removeItem('guestCart');
  } catch (error) {
    console.error('Error clearing guest cart:', error);
  }
};

// Vendég-tétel ideiglenes azonosítója: időbélyeg-alapú, de szigorúan növekvő,
// így két, ugyanabban az ezredmásodpercben felvett tétel sem kap azonos id-t.
let lastGuestItemId = 0;
const nextGuestItemId = () => {
  lastGuestItemId = Math.max(Date.now(), lastGuestItemId + 1);
  return lastGuestItemId;
};

const sumQuantity = (items: CartItem[]) => items.reduce((total, item) => total + item.quantity, 0);
const sumPrice = (items: CartItem[]) => items.reduce((total, item) => total + item.total_price, 0);
const sumGroupDiscount = (items: CartItem[]) =>
  items.reduce((total, item) => total + (item.group_discount_total ?? 0), 0);

const resetGroupDiscount = (state: CartState) => {
  state.groupDiscountAmount = 0;
  state.groupDiscountLabel = null;
  state.couponBlocked = false;
};

/** Szerver-kosár válasz → állapot. Csoport-kedvezményes kosárban a nem-GTP kupon lekerül, jelzéssel. */
const applyServerCart = (state: CartState, cartData: CartResponse) => {
  state.items = cartData.items || [];
  state.totalItems = cartData.total_items || 0;
  state.totalPrice = cartData.total_price || 0;
  state.groupDiscountAmount = cartData.group_discount_amount || 0;
  state.groupDiscountLabel = cartData.group_discount_label ?? null;
  state.couponBlocked = Boolean(cartData.coupon_blocked);
  if (!state.couponBlocked) {
    // Csoport-kedvezmény nélkül a korábbi levétel-jelzés már nem aktuális.
    state.couponNotice = null;
  } else if (state.appliedCoupon && !state.appliedCoupon.isLoyalty) {
    state.appliedCoupon = null;
    state.couponError = null;
    state.couponNotice = GROUP_COUPON_REMOVED_NOTICE;
  }
  state.isGuest = false; // Server cart is now active
};

const saveRollback = (state: CartState, requestId: string, entry: CartRollback) => {
  state.pendingRollback = { ...(state.pendingRollback || {}), [requestId]: entry };
};
const takeRollback = (state: CartState, requestId: string): CartRollback | undefined => {
  const entry = state.pendingRollback?.[requestId];
  if (state.pendingRollback && entry) delete state.pendingRollback[requestId];
  return entry;
};

const guestItems = loadGuestCart();
const initialState: CartState = {
  items: guestItems,
  totalItems: guestItems.reduce((total, item) => total + item.quantity, 0),
  totalPrice: guestItems.reduce((total, item) => total + item.total_price, 0),
  isOpen: false,
  isGuest: true,
  appliedCoupon: null,
  couponLoading: false,
  couponError: null,
  groupDiscountAmount: 0,
  groupDiscountLabel: null,
  couponBlocked: false,
  couponNotice: null,
  pendingRollback: {},
};

// Async thunks
export const fetchCart = createAsyncThunk<CartResponse>(
  'cart/fetchCart',
  async () => {
    const response = await api.get<CartResponse>('/cart/');
    return response.data;
  }
);

// A szerver magyar üzenete az elsődleges; válasz nélkül (hálózati hiba) magyar szöveg, nem az axios angolja.
const cartErrorDetail = (err: any): string | undefined => {
  const fromServer = err?.response?.data?.error || err?.response?.data?.detail;
  if (fromServer) return fromServer;
  if (!err?.response) return 'Hálózati hiba — ellenőrizd a kapcsolatot, és próbáld újra.';
  // Van HTTP-válasz, de nincs szerver-üzenet: az angol axios-szöveg helyett a thunk magyar fallbackje.
  return '';
};

export const addToCartAsync = createAsyncThunk(
  'cart/addToCartAsync',
  async (
    data: {
      product_id: number;
      variant_id?: number;
      quantity: number;
      custom_data?: {
        custom_text?: string;
        custom_font?: string;
        custom_color?: string;
        custom_logo?: string;
      };
      selected_options?: number[];
      sos?: boolean;
      gift_wrap?: boolean;
      custom_design_id?: number;
    },
    thunkAPI,
  ) => {
    try {
      const response = await api.post('/cart/add/', data);
      return response.data;
    } catch (err: any) {
      // Propagate the server's Hungarian error text (e.g. "Csak 5 db elérhető…")
      // instead of Axios's generic HTTP status message.
      const detail = cartErrorDetail(err);
      return thunkAPI.rejectWithValue(detail || 'Kosárba tétel sikertelen');
    }
  }
);

export const removeFromCartAsync = createAsyncThunk(
  'cart/removeFromCartAsync',
  async (itemId: number) => {
    await api.delete(`/cart/items/${itemId}/`);
    return itemId;
  }
);

export const updateQuantityAsync = createAsyncThunk(
  'cart/updateQuantityAsync',
  async (
    { itemId, quantity }: { itemId: number; quantity: number },
    thunkAPI,
  ) => {
    try {
      const response = await api.patch(`/cart/items/${itemId}/`, { quantity });
      return response.data;
    } catch (err: any) {
      const detail = cartErrorDetail(err);
      return thunkAPI.rejectWithValue(detail || 'Mennyiség módosítása sikertelen');
    }
  }
);

export const clearCartAsync = createAsyncThunk(
  'cart/clearCartAsync',
  async () => {
    await api.delete('/cart/');
    return;
  }
);

// Apply coupon
export const applyCouponAsync = createAsyncThunk<
  CouponValidationResponse,
  { code: string },
  { rejectValue: string }
>(
  'cart/applyCoupon',
  async ({ code }, { rejectWithValue }) => {
    try {
      const response = await validateCoupon(code);
      // Védekező: 200-as válaszban is előfordulhatna { valid: false }.
      if (response.data && response.data.valid === false) {
        return rejectWithValue(response.data.message || 'Érvénytelen kuponkód');
      }
      return response.data;
    } catch (err: any) {
      // A backend a body-ban barátságos magyar üzenetet küld (message),
      // akár 404 (ismeretlen kód), akár 400 (lejárt / min. érték / stb.) a státusz.
      const backendMessage =
        err?.response?.data?.message || err?.response?.data?.detail;
      return rejectWithValue(backendMessage || 'Érvénytelen vagy lejárt kuponkód');
    }
  }
);

// Sync guest cart to server on login
export const syncGuestCartAsync = createAsyncThunk<CartResponse, CartItem[]>(
  'cart/syncGuestCartAsync',
  async (guestItems: CartItem[]) => {
    // Send each guest cart item to the server
    for (const item of guestItems) {
      try {
        await api.post('/cart/add/', {
          product_id: item.product.id,
          variant_id: item.product_variant?.id,
          quantity: item.quantity,
          custom_data: {
            custom_text: item.custom_text,
            custom_font: item.custom_font,
            custom_color: item.custom_color,
            custom_logo: item.custom_logo,
          },
          selected_options: item.selected_options?.map(opt => opt.id) || []
        });
      } catch (error) {
        console.error('Error syncing cart item:', error);
      }
    }
    // A feltöltött tételek már a szerveren vannak: a vendég-tárolót MOST
    // ürítjük, nem csak a sikeres lekérés után — különben egy elbukott GET
    // után a következő bejelentkezés újra feltöltené őket (dupla mennyiség).
    clearGuestCartStorage();
    // Fetch the full cart from server after syncing
    const response = await api.get<CartResponse>('/cart/');
    return response.data;
  }
);

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCart: (state, action: PayloadAction<{
      product: Product;
      variant?: ProductVariant;
      quantity?: number;
      customization?: {
        custom_text?: string;
        custom_font?: string;
        custom_color?: string;
        custom_logo?: string;
        selected_options?: CustomizationOption[];
      };
    }>) => {
      const { product, variant, quantity = 1, customization } = action.payload;
      // Nem pozitív mennyiség nem módosíthatja a kosarat (0-s sor / negatív összeg).
      if (!(quantity > 0)) return;

      // Calculate customization cost using gross_price (with VAT)
      const customizationCost = customization?.selected_options?.reduce(
        (sum, option) => {
          const optionPrice = typeof option.gross_price === 'string' ? parseFloat(option.gross_price) : (option.gross_price || 0);
          return sum + optionPrice;
        }, 0
      ) || 0;

      // Check if item already exists (including customizations).
      // Two items merge only if product + variant + custom_text + custom_font +
      // custom_color + custom_logo AND the sorted list of selected_option ids
      // all match — otherwise "different customizations" fold into one cart line
      // (pl. ugyanaz a felirat más színnel külön tétel).
      const newOptionIds = (customization?.selected_options || [])
        .map((o) => o.id)
        .sort()
        .join(',');
      const existingItemIndex = state.items.findIndex((item) => {
        if (item.product.id !== product.id) return false;
        if (item.product_variant?.id !== variant?.id) return false;
        if (item.custom_text !== customization?.custom_text) return false;
        if (item.custom_font !== customization?.custom_font) return false;
        if (item.custom_color !== customization?.custom_color) return false;
        if (item.custom_logo !== customization?.custom_logo) return false;
        const existingOptionIds = (item.selected_options || [])
          .map((o: any) => o.id)
          .sort()
          .join(',');
        return existingOptionIds === newOptionIds;
      });

      if (existingItemIndex >= 0) {
        // Update existing item
        state.items[existingItemIndex].quantity += quantity;
        state.items[existingItemIndex].total_price =
          state.items[existingItemIndex].unit_price * state.items[existingItemIndex].quantity;
      } else {
        // Add new item — prefer the current_final_price so an active sale
        // (variant-level OR product-level) is honored in the guest cart.
        // Falls back to plain final_price / gross_price when the sale field
        // isn't populated on this variant/product.
        const productSalePrice = (product as any).current_gross_price ?? product.gross_price;
        const productBasePrice = typeof productSalePrice === 'string' ?
          parseFloat(productSalePrice as any) : (productSalePrice || 0);
        const variantSalePrice = variant
          ? ((variant as any).current_final_price ?? variant.final_price)
          : undefined;
        const variantFinalPrice = variantSalePrice !== undefined
          ? (typeof variantSalePrice === 'string' ? parseFloat(variantSalePrice as any) : (variantSalePrice || productBasePrice))
          : productBasePrice;
        const unit_price = variantFinalPrice + customizationCost;

        const newItem: CartItem = {
          id: nextGuestItemId(), // Temporary ID for guest cart (egyedi, monoton)
          product,
          product_variant: variant,
          quantity,
          unit_price,
          total_price: unit_price * quantity,
          custom_text: customization?.custom_text,
          custom_font: customization?.custom_font,
          custom_color: customization?.custom_color,
          custom_logo: customization?.custom_logo,
          selected_options: customization?.selected_options,
        };
        state.items.push(newItem);
      }

      // Recalculate totals
      state.totalItems = state.items.reduce((total, item) => total + item.quantity, 0);
      state.totalPrice = state.items.reduce((total, item) => total + item.total_price, 0);

      // Save to localStorage if guest cart
      if (state.isGuest) {
        saveGuestCart(state.items);
      }
    },

    removeFromCart: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter(item => item.id !== action.payload);

      // Recalculate totals
      state.totalItems = state.items.reduce((total, item) => total + item.quantity, 0);
      state.totalPrice = state.items.reduce((total, item) => total + item.total_price, 0);

      // Save to localStorage if guest cart
      if (state.isGuest) {
        saveGuestCart(state.items);
      }
    },

    updateQuantity: (state, action: PayloadAction<{ id: number; quantity: number }>) => {
      const { id, quantity } = action.payload;
      const item = state.items.find(item => item.id === id);

      if (item) {
        if (quantity <= 0) {
          state.items = state.items.filter(item => item.id !== id);
        } else {
          item.quantity = quantity;
          item.total_price = item.unit_price * quantity;
        }

        // Recalculate totals
        state.totalItems = state.items.reduce((total, item) => total + item.quantity, 0);
        state.totalPrice = state.items.reduce((total, item) => total + item.total_price, 0);

        // Save to localStorage if guest cart
        if (state.isGuest) {
          saveGuestCart(state.items);
        }
      }
    },

    clearCart: (state) => {
      state.items = [];
      state.totalItems = 0;
      state.totalPrice = 0;
      resetGroupDiscount(state);

      // Clear localStorage if guest cart
      if (state.isGuest) {
        clearGuestCartStorage();
      }
    },

    toggleCart: (state) => {
      state.isOpen = !state.isOpen;
    },

    openCart: (state) => {
      state.isOpen = true;
    },

    closeCart: (state) => {
      state.isOpen = false;
    },

    removeCoupon: (state) => {
      state.appliedCoupon = null;
      state.couponError = null;
    },

    dismissCouponNotice: (state) => {
      state.couponNotice = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCart.fulfilled, (state, action) => {
        applyServerCart(state, action.payload);
      })
      // Optimistic UI: Add to cart
      .addCase(addToCartAsync.pending, (state, action) => {
        // Optimistically update the total items count
        // Note: We don't add the item to the list yet because we need server response for item ID
        state.totalItems += action.meta.arg.quantity;
      })
      .addCase(addToCartAsync.fulfilled, (state, action) => {
        // After adding to cart, refresh the total items and price
        state.totalItems = action.payload.cart_items || state.totalItems;
        state.totalPrice = action.payload.cart_total || state.totalPrice;
        state.isGuest = false; // Server cart is now active
      })
      .addCase(addToCartAsync.rejected, (state, action) => {
        // Rollback optimistic update
        state.totalItems -= action.meta.arg.quantity;
        console.error('Add to cart failed:', action.error);
      })
      // Optimistic UI: Remove from cart
      .addCase(removeFromCartAsync.pending, (state, action) => {
        // Optimistically remove the item
        const index = state.items.findIndex(item => item.id === action.meta.arg);
        const itemToRemove = index >= 0 ? state.items[index] : undefined;
        if (itemToRemove) {
          saveRollback(state, action.meta.requestId, { removed: { item: { ...itemToRemove }, index } });
          state.items = state.items.filter(item => item.id !== action.meta.arg);
          state.totalItems -= itemToRemove.quantity;
          state.totalPrice -= itemToRemove.total_price;
          state.groupDiscountAmount = sumGroupDiscount(state.items);
        }
      })
      .addCase(removeFromCartAsync.fulfilled, (state, action) => {
        // Item already removed optimistically, just ensure consistency
        takeRollback(state, action.meta.requestId);
        const itemId = action.payload;
        state.items = state.items.filter(item => item.id !== itemId);
        state.totalItems = state.items.reduce((total, item) => total + item.quantity, 0);
        state.totalPrice = state.items.reduce((total, item) => total + item.total_price, 0);
      })
      .addCase(removeFromCartAsync.rejected, (state, action) => {
        // Visszagörgetés: az optimistán törölt tétel visszakerül az eredeti helyére.
        const removed = takeRollback(state, action.meta.requestId)?.removed;
        if (removed && !state.items.some((item) => item.id === removed.item.id)) {
          state.items.splice(Math.min(removed.index, state.items.length), 0, removed.item);
          state.totalItems = sumQuantity(state.items);
          state.totalPrice = sumPrice(state.items);
          state.groupDiscountAmount = sumGroupDiscount(state.items);
        }
        console.error('Remove from cart failed:', action.error);
      })
      // Optimistic UI: Update quantity
      .addCase(updateQuantityAsync.pending, (state, action) => {
        // Optimistically update the quantity
        const item = state.items.find(item => item.id === action.meta.arg.itemId);
        if (item) {
          saveRollback(state, action.meta.requestId, {
            quantity: { itemId: item.id, quantity: item.quantity, totalPrice: item.total_price },
          });
          const oldQuantity = item.quantity;
          const newQuantity = action.meta.arg.quantity;
          item.quantity = newQuantity;
          item.total_price = item.unit_price * newQuantity;
          if (item.group_discount_per_unit) {
            item.group_discount_total = item.group_discount_per_unit * newQuantity;
            state.groupDiscountAmount = sumGroupDiscount(state.items);
          }

          state.totalItems += (newQuantity - oldQuantity);
          state.totalPrice += (item.unit_price * (newQuantity - oldQuantity));
        }
      })
      .addCase(updateQuantityAsync.fulfilled, (state, action) => {
        // Az optimista mennyiség marad; a visszagörgetési adat már nem kell.
        takeRollback(state, action.meta.requestId);
      })
      .addCase(updateQuantityAsync.rejected, (state, action) => {
        // Visszagörgetés: a tétel a kérés előtti mennyiségére áll vissza.
        const previous = takeRollback(state, action.meta.requestId)?.quantity;
        const item = previous && state.items.find((i) => i.id === previous.itemId);
        if (previous && item) {
          item.quantity = previous.quantity;
          item.total_price = previous.totalPrice;
          if (item.group_discount_per_unit) {
            item.group_discount_total = item.group_discount_per_unit * previous.quantity;
            state.groupDiscountAmount = sumGroupDiscount(state.items);
          }
          state.totalItems = sumQuantity(state.items);
          state.totalPrice = sumPrice(state.items);
        }
        console.error('Update quantity failed:', action.error);
      })
      // Optimistic UI: Clear cart
      .addCase(clearCartAsync.pending, (state, action) => {
        // Optimistically clear the cart
        saveRollback(state, action.meta.requestId, { clearedItems: state.items });
        state.items = [];
        state.totalItems = 0;
        state.totalPrice = 0;
        state.groupDiscountAmount = 0;
      })
      .addCase(clearCartAsync.fulfilled, (state, action) => {
        // After clearing cart, reset local state (already done optimistically)
        takeRollback(state, action.meta.requestId);
        state.items = [];
        state.totalItems = 0;
        state.totalPrice = 0;
        resetGroupDiscount(state);
      })
      .addCase(clearCartAsync.rejected, (state, action) => {
        // Visszagörgetés: a kiürítés előtti tartalom áll vissza (ha közben nem
        // került új tétel a kosárba).
        const clearedItems = takeRollback(state, action.meta.requestId)?.clearedItems;
        if (clearedItems && state.items.length === 0) {
          state.items = clearedItems;
          state.totalItems = sumQuantity(state.items);
          state.totalPrice = sumPrice(state.items);
          state.groupDiscountAmount = sumGroupDiscount(state.items);
        }
        console.error('Clear cart failed:', action.error);
      })
      .addCase(syncGuestCartAsync.fulfilled, (state, action) => {
        // After syncing guest cart, load server cart
        applyServerCart(state, action.payload);
        // Clear guest cart from localStorage
        clearGuestCartStorage();
      })
      // Coupon handling
      .addCase(applyCouponAsync.pending, (state) => {
        state.couponLoading = true;
        state.couponError = null;
        state.couponNotice = null;
      })
      .addCase(applyCouponAsync.fulfilled, (state, action) => {
        state.couponLoading = false;
        state.appliedCoupon = {
          code: action.meta.arg.code,
          discountAmount: action.payload.discount_amount || 0,
          message: action.payload.message,
          eligibleItemIds: action.payload.eligible_item_ids ?? null,
          // GT Pont (GTP) kupon — kizárólag a szerver jelzése alapján.
          isLoyalty: action.payload.is_loyalty ?? false,
        };
      })
      .addCase(applyCouponAsync.rejected, (state, action) => {
        state.couponLoading = false;
        // A rejectWithValue payload a backend barátságos üzenete; csak végső
        // esetben esünk vissza a nyers axios hibaszövegre.
        state.couponError =
          action.payload || action.error.message || 'Kupon alkalmazása sikertelen';
        state.appliedCoupon = null;
      });
  },
});

export const {
  addToCart,
  removeFromCart,
  updateQuantity,
  clearCart,
  toggleCart,
  openCart,
  closeCart,
  removeCoupon,
  dismissCouponNotice,
} = cartSlice.actions;

export default cartSlice.reducer;